import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import {
  useFoodicsStatus,
  useDisconnectFoodics,
  useFoodicsBranches,
  useFoodicsBranchMappings,
  useFoodicsMenuEntities,
  useFoodicsPaymentMethods,
  useFoodicsPaymentMethodMappings,
  useImportAllFoodicsInventory,
  useImportAllFoodicsProduction,
  useImportAllFoodicsMenu,
} from "src/api/integrations/foodics";
import { isProductionItem } from "src/types/foodics";
import { getFoodicsInstallUrl } from "./constants";
import { getLocalStoredData } from "src/utils/_localStorage";
import { ConstLocalStorage } from "src/constants/_localStorage";
import { notificationsState } from "src/store/notifications";
import { ConfirmPopup } from "src/shared/components/confirm-popup";
import { BranchMappingModal } from "./branch-mapping-modal";
import { PaymentMethodMappingModal } from "./payment-method-mapping-modal";
import { ProductionImportModal } from "./production-import-modal";
import { MenuImportModal } from "./menu-import-modal";
import i18n from "src/i18n";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import SoupKitchenIcon from "@mui/icons-material/SoupKitchen";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import PaymentIcon from "@mui/icons-material/Payment";
import foodicsLogo from "src/assets/svg-icons/foodics.svg";

type WorkspaceStepStatus = "completed" | "inProgress" | "pending";

type WorkspaceStepCardProps = {
  stepNumber: number;
  icon: ReactNode;
  title: string;
  subtitle: string;
  status: WorkspaceStepStatus;
  isActive: boolean;
  actions: ReactNode;
  showConnector: boolean;
};

const stepStatusChip = (
  status: WorkspaceStepStatus,
  t: (key: string) => string
) => {
  if (status === "completed") {
    return (
      <Chip
        size="small"
        label={t("foodicsWorkspace.statusCompleted")}
        sx={{ bgcolor: "#D1FADF", color: "#027A48", fontWeight: 500 }}
      />
    );
  }
  if (status === "inProgress") {
    return (
      <Chip
        size="small"
        label={t("foodicsWorkspace.statusInProgress")}
        sx={{ bgcolor: "#FDEAD7", color: "#B93815", fontWeight: 500 }}
      />
    );
  }
  return null;
};

const WorkspaceStepCard = ({
  stepNumber,
  icon,
  title,
  subtitle,
  status,
  isActive,
  actions,
  showConnector,
}: WorkspaceStepCardProps) => {
  const { t } = useTranslation("integrations");

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: "12px",
          p: { xs: 2, sm: 3 },
          ...(isActive && {
            borderColor: "#FF8D3F",
            boxShadow: "0px 4px 12px rgba(255, 141, 63, 0.15)",
          }),
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "flex-start", md: "center" }}
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minWidth={40}
              height={40}
              borderRadius="50%"
              bgcolor={
                status === "completed"
                  ? "#12B76A"
                  : isActive
                  ? "#FF8D3F"
                  : "#F2F4F7"
              }
              color={
                status === "completed" || isActive ? "#FFFFFF" : "#667085"
              }
            >
              {status === "completed" ? (
                <CheckIcon fontSize="small" />
              ) : (
                <Typography fontSize={16} fontWeight={600} color="inherit">
                  {stepNumber}
                </Typography>
              )}
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minWidth={48}
              height={48}
              bgcolor="#fbeada"
              borderRadius="8px"
              sx={{ "& svg": { color: "#FF8D3F" } }}
            >
              {icon}
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontSize={16} fontWeight={600} color="#101828">
                  {title}
                </Typography>
                {stepStatusChip(status, t)}
              </Stack>
              <Typography fontSize={14} color="#667085">
                {subtitle}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {actions}
          </Stack>
        </Stack>
      </Paper>
      {showConnector && (
        <Box width="2px" height={16} bgcolor="#EAECF0" ml="39px" />
      )}
    </Box>
  );
};

const FoodicsSettings = () => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [paymentMethodMappingModalOpen, setPaymentMethodMappingModalOpen] = useState(false);
  const [productionModalOpen, setProductionModalOpen] = useState(false);
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const isArabic = i18n.language === "ar";

  const { data: status, isLoading, refetch } = useFoodicsStatus();
  const { mutate: disconnect, status: disconnectStatus } =
    useDisconnectFoodics();

  const isConnected = !!(status?.connected && status?.is_active);

  const {
    data: foodicsBranchesData,
    isLoading: foodicsBranchesLoading,
    refetch: refetchFoodicsBranches,
  } = useFoodicsBranches(isConnected);
  const {
    data: mappingsData,
    isLoading: mappingsLoading,
    refetch: refetchMappings,
  } = useFoodicsBranchMappings(isConnected);

  const foodicsBranches = foodicsBranchesData?.data ?? [];
  const mappings = mappingsData?.data ?? [];
  const mappedFoodicsBranchIds = mappings.map((mapping) => mapping.external_id);
  const allBranchesMapped =
    foodicsBranches.length > 0 &&
    foodicsBranches.every((branch) =>
      mappedFoodicsBranchIds.includes(branch.id)
    );

  // First page (200 rows) is enough to tell whether the inventory import already ran.
  const {
    data: inventoryEntitiesData,
    isSuccess: inventoryEntitiesLoaded,
    isFetching: inventoryEntitiesFetching,
    refetch: refetchInventoryEntities,
  } = useFoodicsMenuEntities(
    {
      include_inventory: 1,
      inventory_page: 1,
      inventory_per_page: 200,
      products_per_page: 1,
      categories_per_page: 1,
    },
    isConnected && allBranchesMapped
  );
  const allInventoryEntities = inventoryEntitiesData?.data?.inventory_items ?? [];
  const inventoryFetchFailed =
    inventoryEntitiesData?.data?.inventory_fetch_failed === true;
  // Step 2 covers plain items (no ingredients); step 3 covers productions (has ingredients).
  const inventoryItems = allInventoryEntities.filter(
    (item) => !isProductionItem(item)
  );
  const productionItems = allInventoryEntities.filter(
    (item) => isProductionItem(item)
  );
  // Statuses are unsettled until the data behind them has loaded.
  const branchesStatusSettled = !foodicsBranchesLoading && !mappingsLoading;
  const importStatusesSettled =
    allBranchesMapped && inventoryEntitiesLoaded && !inventoryFetchFailed;
  // Nothing left (or nothing at all) to import means the step is done.
  const inventoryCompleted =
    importStatusesSettled &&
    inventoryItems.every((item) => item.already_imported);
  const productionCompleted =
    importStatusesSettled &&
    productionItems.every((item) => item.already_imported);

  // Menu entities (categories, products, modifiers) — for step 4 status.
  const {
    data: menuEntitiesData,
    isSuccess: menuEntitiesLoaded,
    isFetching: menuEntitiesFetching,
    refetch: refetchMenuEntities,
  } = useFoodicsMenuEntities(
    {
      products_page: 1,
      products_per_page: 200,
      categories_page: 1,
      categories_per_page: 200,
      include_modifier_options: 1,
      modifier_options_page: 1,
      modifier_options_per_page: 200,
    },
    isConnected && allBranchesMapped && productionCompleted
  );
  const menuCategories = menuEntitiesData?.data?.categories ?? [];
  const menuProducts = menuEntitiesData?.data?.products ?? [];
  const menuModifiers = menuEntitiesData?.data?.modifier_options ?? [];
  const menuStatusSettled =
    productionCompleted && menuEntitiesLoaded;
  const menuCompleted =
    menuStatusSettled &&
    menuCategories.every((c) => c.already_imported) &&
    menuProducts.every((p) => p.already_imported) &&
    menuModifiers.every((m) => m.already_imported);

  // Payment method mapping — step 5.
  const {
    data: foodicsPaymentMethodsData,
    isLoading: foodicsPaymentMethodsLoading,
    refetch: refetchFoodicsPaymentMethods,
  } = useFoodicsPaymentMethods(isConnected && menuCompleted);
  const {
    data: paymentMethodMappingsData,
    isLoading: paymentMethodMappingsLoading,
    refetch: refetchPaymentMethodMappings,
  } = useFoodicsPaymentMethodMappings(isConnected && menuCompleted);

  const foodicsPaymentMethods = foodicsPaymentMethodsData?.data ?? [];
  const paymentMethodMappings = paymentMethodMappingsData?.data ?? [];
  const mappedFoodicsPaymentMethodIds = paymentMethodMappings.map(
    (m) => m.external_id
  );
  const paymentMethodStatusSettled =
    menuCompleted &&
    !foodicsPaymentMethodsLoading &&
    !paymentMethodMappingsLoading;
  const allPaymentMethodsMapped =
    foodicsPaymentMethods.length > 0 &&
    foodicsPaymentMethods.every((pm) =>
      mappedFoodicsPaymentMethodIds.includes(pm.id)
    );

  const { mutate: importAllInventory, status: importAllStatus } =
    useImportAllFoodicsInventory();
  const { mutate: importAllProduction, status: importAllProductionStatus } =
    useImportAllFoodicsProduction();
  const { mutate: importAllMenu, status: importAllMenuStatus } =
    useImportAllFoodicsMenu();

  const branchesStatus: WorkspaceStepStatus = !branchesStatusSettled
    ? "pending"
    : allBranchesMapped
    ? "completed"
    : "inProgress";
  const inventoryStatus: WorkspaceStepStatus = !importStatusesSettled
    ? "pending"
    : inventoryCompleted
    ? "completed"
    : "inProgress";
  const productionStatus: WorkspaceStepStatus = !importStatusesSettled
    ? "pending"
    : productionCompleted
    ? "completed"
    : "inProgress";
  const menuStatus: WorkspaceStepStatus = !menuStatusSettled
    ? "pending"
    : menuCompleted
    ? "completed"
    : "inProgress";
  const paymentMethodStatus: WorkspaceStepStatus = !paymentMethodStatusSettled
    ? "pending"
    : allPaymentMethodsMapped
    ? "completed"
    : "inProgress";
  const activeStepNumber = !allBranchesMapped
    ? 1
    : !inventoryCompleted
    ? 2
    : !productionCompleted
    ? 3
    : !menuCompleted
    ? 4
    : 5;

  const forwardArrow = (
    <ArrowForwardIcon
      sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
      fontSize="small"
    />
  );

  const handleConnect = () => {
    const user = getLocalStoredData(ConstLocalStorage.user);
    const companyId = Number(user?.company?.id ?? user?.company_id ?? 0);
    if (!companyId) {
      return;
    }
    const url = getFoodicsInstallUrl(companyId);
    if (!url) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: generalT("apiBaseUrlMissing"),
        },
      ]);
      return;
    }
    window.open(url, "_blank");
    setIsPolling(true);
  };

  useEffect(() => {
    if (isPolling && !isConnected) {
      pollingRef.current = setInterval(() => {
        refetch();
      }, 2500);
    }

    if (isConnected && isPolling) {
      setIsPolling(false);
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("callback.success"),
        },
      ]);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [isPolling, isConnected, refetch]);

  const handleDisconnect = () => {
    disconnect();
  };

  useEffect(() => {
    if (disconnectStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("callback.success"),
        },
      ]);
      setDisconnectDialogOpen(false);
      refetch();
    } else if (disconnectStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("errors.disconnectFailed"),
        },
      ]);
    }
  }, [disconnectStatus]);

  useEffect(() => {
    if (importAllStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("foodicsWorkspace.directImportSuccess"),
        },
      ]);
      refetchInventoryEntities();
    } else if (importAllStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("foodicsWorkspace.directImportError"),
        },
      ]);
    }
  }, [importAllStatus]);

  useEffect(() => {
    if (importAllProductionStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("foodicsWorkspace.productionImport.directImportSuccess"),
        },
      ]);
      setProductionModalOpen(false);
      refetchInventoryEntities();
    } else if (importAllProductionStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("foodicsWorkspace.productionImport.directImportError"),
        },
      ]);
    }
  }, [importAllProductionStatus]);

  useEffect(() => {
    if (importAllMenuStatus === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: t("foodicsWorkspace.menuImport.directImportSuccess"),
        },
      ]);
      setMenuModalOpen(false);
      refetchMenuEntities();
    } else if (importAllMenuStatus === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: t("foodicsWorkspace.menuImport.directImportError"),
        },
      ]);
    }
  }, [importAllMenuStatus]);

  const handleCloseMappingModal = () => {
    setMappingModalOpen(false);
    refetchMappings();
    refetchFoodicsBranches();
  };

  if (isLoading) {
    return (
      <Stack
        gap={3}
        p={{ xs: 2, sm: "8px 32px 32px 32px" }}
        alignItems="center"
        justifyContent="center"
        minHeight={400}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack gap={3} p={{ xs: 2, sm: "8px 32px 32px 32px" }}>
      {isConnected ? (
        <>
          {/* Connected header card */}
          <Paper variant="outlined" sx={{ borderRadius: "12px", p: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              flexWrap="wrap"
              gap={2}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Button
                  variant="outlined"
                  color="tertiary"
                  startIcon={
                    <ArrowBackIcon
                      sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
                    />
                  }
                  onClick={() => navigate("/integrations")}
                >
                  {generalT("back")}
                </Button>
                <Box
                  component="img"
                  src={foodicsLogo}
                  alt="Foodics"
                  width={48}
                  height={48}
                  sx={{ objectFit: "contain" }}
                />
                <Box>
                  <Typography fontSize={18} fontWeight={600} color="#101828">
                    {t("foodicsWorkspace.connectedTitle")}
                  </Typography>
                  {status?.business_name && (
                    <Typography fontSize={14} color="#667085">
                      {status.business_name}
                    </Typography>
                  )}
                  {status?.connected_at && (
                    <Typography fontSize={14} color="#667085">
                      {t("foodics.connectedAt")}{" "}
                      {new Date(status.connected_at).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Chip
                  size="small"
                  label={t("status.active")}
                  sx={{ bgcolor: "#D1FADF", color: "#027A48", fontWeight: 500 }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<LinkOffIcon />}
                  onClick={() => setDisconnectDialogOpen(true)}
                >
                  {t("actions.disconnect")}
                </Button>
              </Stack>
            </Box>
          </Paper>

          {/* Workspace steps */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-end"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography fontSize={18} fontWeight={600} color="#101828">
                {t("foodicsWorkspace.sectionTitle")}
              </Typography>
              <Typography fontSize={14} color="#667085">
                {t("foodicsWorkspace.sectionSubtitle")}
              </Typography>
            </Box>
            <Typography fontSize={14} color="#667085">
              {t("foodicsWorkspace.stepOf", {
                current: activeStepNumber,
                total: 5,
              })}
            </Typography>
          </Box>

          <Stack>
            <WorkspaceStepCard
              stepNumber={1}
              icon={<AccountTreeIcon />}
              title={t("foodicsWorkspace.steps.branches.title")}
              subtitle={t("foodicsWorkspace.steps.branches.subtitle")}
              status={branchesStatus}
              isActive={activeStepNumber === 1}
              showConnector
              actions={
                <Button
                  variant={allBranchesMapped ? "outlined" : "contained"}
                  color={allBranchesMapped ? "tertiary" : "primary"}
                  endIcon={forwardArrow}
                  disabled={!branchesStatusSettled}
                  onClick={() => setMappingModalOpen(true)}
                >
                  {t("foodicsWorkspace.clickToMap")}
                </Button>
              }
            />
            <WorkspaceStepCard
              stepNumber={2}
              icon={<Inventory2Icon />}
              title={t("foodicsWorkspace.steps.inventory.title")}
              subtitle={t("foodicsWorkspace.steps.inventory.subtitle")}
              status={inventoryStatus}
              isActive={activeStepNumber === 2}
              showConnector
              actions={
                <>
                  <Button
                    variant="outlined"
                    color="tertiary"
                    startIcon={<EditOutlinedIcon fontSize="small" />}
                    disabled={
                      !importStatusesSettled ||
                      inventoryEntitiesFetching ||
                      importAllStatus === "pending"
                    }
                    onClick={() => navigate("/integrations/foodics/inventory/edit")}
                  >
                    {t("foodicsWorkspace.editBeforeImport")}
                  </Button>
                  <Button
                    variant={activeStepNumber === 2 ? "contained" : "outlined"}
                    color={activeStepNumber === 2 ? "primary" : "tertiary"}
                    endIcon={
                      importAllStatus === "pending" ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        forwardArrow
                      )
                    }
                    disabled={
                      !importStatusesSettled ||
                      inventoryEntitiesFetching ||
                      importAllStatus === "pending"
                    }
                    onClick={() => importAllInventory()}
                  >
                    {t("foodicsWorkspace.importInventory")}
                  </Button>
                </>
              }
            />
            <WorkspaceStepCard
              stepNumber={3}
              icon={<SoupKitchenIcon />}
              title={t("foodicsWorkspace.steps.production.title")}
              subtitle={t("foodicsWorkspace.steps.production.subtitle")}
              status={productionStatus}
              isActive={activeStepNumber === 3}
              showConnector
              actions={
                <>
                  <Button
                    variant="outlined"
                    color="tertiary"
                    startIcon={<EditOutlinedIcon fontSize="small" />}
                    disabled={
                      !importStatusesSettled ||
                      inventoryEntitiesFetching ||
                      importAllProductionStatus === "pending"
                    }
                    onClick={() =>
                      navigate("/integrations/foodics/production/edit")
                    }
                  >
                    {t("foodicsWorkspace.editBeforeImport")}
                  </Button>
                  <Button
                    variant={activeStepNumber === 3 ? "contained" : "outlined"}
                    color={activeStepNumber === 3 ? "primary" : "tertiary"}
                    endIcon={
                      importAllProductionStatus === "pending" ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        forwardArrow
                      )
                    }
                    disabled={
                      !importStatusesSettled ||
                      inventoryEntitiesFetching ||
                      importAllProductionStatus === "pending"
                    }
                    onClick={() => setProductionModalOpen(true)}
                  >
                    {t("foodicsWorkspace.importProduction")}
                  </Button>
                </>
              }
            />
            <WorkspaceStepCard
              stepNumber={4}
              icon={<RestaurantMenuIcon />}
              title={t("foodicsWorkspace.steps.menu.title")}
              subtitle={t("foodicsWorkspace.steps.menu.subtitle")}
              status={menuStatus}
              isActive={activeStepNumber === 4}
              showConnector
              actions={
                <>
                  <Button
                    variant="outlined"
                    color="tertiary"
                    startIcon={<EditOutlinedIcon fontSize="small" />}
                    disabled={
                      !menuStatusSettled ||
                      menuEntitiesFetching ||
                      importAllMenuStatus === "pending"
                    }
                    onClick={() =>
                      navigate("/integrations/foodics/menu/edit")
                    }
                  >
                    {t("foodicsWorkspace.editBeforeImport")}
                  </Button>
                  <Button
                    variant={activeStepNumber === 4 ? "contained" : "outlined"}
                    color={activeStepNumber === 4 ? "primary" : "tertiary"}
                    endIcon={
                      importAllMenuStatus === "pending" ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        forwardArrow
                      )
                    }
                    disabled={
                      !menuStatusSettled ||
                      menuEntitiesFetching ||
                      importAllMenuStatus === "pending"
                    }
                    onClick={() => setMenuModalOpen(true)}
                  >
                    {t("foodicsWorkspace.importMenu")}
                  </Button>
                </>
              }
            />
            <WorkspaceStepCard
              stepNumber={5}
              icon={<PaymentIcon />}
              title={t("foodicsWorkspace.steps.paymentMethods.title")}
              subtitle={t("foodicsWorkspace.steps.paymentMethods.subtitle")}
              status={paymentMethodStatus}
              isActive={activeStepNumber === 5}
              showConnector={false}
              actions={
                <Button
                  variant={allPaymentMethodsMapped ? "outlined" : "contained"}
                  color={allPaymentMethodsMapped ? "tertiary" : "primary"}
                  endIcon={forwardArrow}
                  disabled={!paymentMethodStatusSettled}
                  onClick={() => setPaymentMethodMappingModalOpen(true)}
                >
                  {t("foodicsWorkspace.clickToMap")}
                </Button>
              }
            />
          </Stack>

          <BranchMappingModal
            open={mappingModalOpen}
            handleClose={handleCloseMappingModal}
            foodicsBranches={foodicsBranches}
            foodicsBranchesLoading={foodicsBranchesLoading}
            mappings={mappings}
            refetchMappings={refetchMappings}
          />

          <PaymentMethodMappingModal
            open={paymentMethodMappingModalOpen}
            handleClose={() => {
              setPaymentMethodMappingModalOpen(false);
              refetchPaymentMethodMappings();
              refetchFoodicsPaymentMethods();
            }}
            foodicsPaymentMethods={foodicsPaymentMethods}
            foodicsPaymentMethodsLoading={foodicsPaymentMethodsLoading}
            mappings={paymentMethodMappings}
            refetchMappings={refetchPaymentMethodMappings}
          />

          <ProductionImportModal
            open={productionModalOpen}
            handleClose={() => setProductionModalOpen(false)}
            isPending={importAllProductionStatus === "pending"}
            onConfirm={(withRecipe) => importAllProduction({ withRecipe })}
          />

          <MenuImportModal
            open={menuModalOpen}
            handleClose={() => setMenuModalOpen(false)}
            isPending={importAllMenuStatus === "pending"}
            onConfirm={(withRecipe) => importAllMenu({ withRecipe })}
          />
        </>
      ) : (
        <>
          {/* Header (not connected) */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                variant="text"
                startIcon={
                  <ArrowBackIcon
                    sx={{ transform: isArabic ? "scaleX(-1)" : "none" }}
                  />
                }
                onClick={() => navigate("/integrations")}
                sx={{ color: "#667085" }}
              >
                {generalT("back")}
              </Button>
              <Box
                component="img"
                src={foodicsLogo}
                alt="Foodics"
                width={48}
                height={48}
                sx={{ objectFit: "contain" }}
              />
              <Box>
                <Typography fontSize={24} fontWeight={600} color="#101828">
                  {t("foodics.title")}
                </Typography>
                <Chip
                  size="small"
                  label={t("status.disconnected")}
                  color="default"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Stack>
          </Box>

          <Box bgcolor="#F2F4F7" width="100%" height="1px" />

          {/* Not Connected State */}
          <Card variant="outlined" sx={{ borderRadius: "12px" }}>
            <CardContent>
              <Stack spacing={3} alignItems="center" py={4}>
                <Box
                  component="img"
                  src={foodicsLogo}
                  alt="Foodics"
                  width={80}
                  height={80}
                  sx={{ objectFit: "contain" }}
                />
                <Box textAlign="center">
                  <Typography fontSize={18} fontWeight={600} color="#101828">
                    {t("foodics.title")}
                  </Typography>
                  <Typography
                    fontSize={14}
                    color="#667085"
                    mt={1}
                    maxWidth={400}
                  >
                    {status?.install_uses_dev_token
                      ? t("foodics.connectPromptDev")
                      : t("foodics.connectPrompt")}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<LinkIcon />}
                  onClick={handleConnect}
                >
                  {t("actions.connect")}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      {/* Disconnect Confirmation Dialog */}
      <ConfirmPopup
        open={disconnectDialogOpen}
        handleClose={() => setDisconnectDialogOpen(false)}
        handleConfirm={handleDisconnect}
        isPending={disconnectStatus === "pending"}
        title={t("foodics.disconnectConfirm")}
        subtitle={t("foodics.disconnectWarning")}
        confirmBtnText={t("actions.disconnect")}
      />
    </Stack>
  );
};

export default FoodicsSettings;
